import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import axios from "axios";
import SiteFooter from "../components/SiteFooter";
import { CityAutocomplete } from "../components/CityAutocomplete";
import { useLang } from "../i18n/LanguageContext";
import { requestPay } from "../utils/pay";
import { saveReport, isInAppBrowser } from "../utils/savePdf";

const Global = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #FBF6EA; font-family: 'Pretendard', 'Malgun Gothic', sans-serif; }
`;

const API_BASE = process.env.REACT_APP_API_URL || "";

function parseTime(val: string) {
  const s = val.replace(/\s/g, "");
  if (!s) return { hour: 99, minute: 0 };
  let h: number, m: number;
  if (s.includes(":")) {
    const parts = s.split(":");
    h = parseInt(parts[0]); m = parseInt(parts[1] || "0");
  } else if (/^\d{3,4}$/.test(s)) {
    const p = s.padStart(4, "0");
    h = parseInt(p.slice(0, 2)); m = parseInt(p.slice(2));
  } else {
    h = parseInt(s); m = 0;
  }
  return {
    hour: (!isNaN(h) && h >= 0 && h <= 23) ? h : 99,
    minute: (!isNaN(m) && m >= 0 && m <= 59) ? m : 0,
  };
}

/* 입력 폼 한 사람 → 서버 요청 형식 (결제 복귀 처리에서도 사용) */
function toPersonPayload(p: any) {
  const d = parseBirthDate(p?.birth_date || "");
  return {
    name: p?.name || "",
    gender: p?.gender || "female",
    birth_year: d.year, birth_month: d.month, birth_day: d.day,
    birth_hour:   p?.birth_time_unknown ? 99 : parseTime(p?.birth_time || "").hour,
    birth_minute: p?.birth_time_unknown ? 0  : parseTime(p?.birth_time || "").minute,
    birth_type: p?.birth_type || "solar",
    city: p?.city || "서울",
  };
}

function parseBirthDate(val: string) {
  const sep = val.includes("/") ? "/" : val.includes(".") ? "." : val.includes("-") ? "-" : null;
  if (sep) { const [y,m,d] = val.split(sep).map(Number); return { year:y||0,month:m||0,day:d||0 }; }
  if (val.length === 8) return { year:Number(val.slice(0,4)),month:Number(val.slice(4,6)),day:Number(val.slice(6,8)) };
  return { year:0,month:0,day:0 };
}

/* 이미지별 카드 배경 위치 */
const IMG_BG_POS: Record<string, string> = {
  "/woo_02.webp": "center 30%",
  "/woo_03.webp": "center 65%",
  "/woo_04.webp": "center 25%",
  "/woo_05.webp": "center 30%",
  "/woo_06.webp": "center 30%",
  "/woo_07.webp": "center 55%",
  "/woo_08.webp": "center 30%",
};

/* ── 서비스 정의 ──────────────────────────────────────────── */
/* 쿠키 사주 = 우연 사주의 '무료 구간'(2026-08-04 개편).
   전 상품 무료이며 결제를 받지 않는다. 결과 아래에서 우연 사주로 이어진다. */
const SERVICES = [
  { key: "퀵사주_연애운",   label: "연애운",  iconImg: "/woo_y_1.webp", price: 0, color: "#D0654F", img: "/woo_08.webp", desc: "이상형을 어디서 만날까?\n어떻게 생겼을까?", needPartner: false },
  { key: "퀵사주_직장운",   label: "직장운",  iconImg: "/woo_y_5.webp", price: 0, color: "#4A7BA8", img: "/woo_05.webp", desc: "나는 무슨 직업이 어울릴까?",                          needPartner: false },
  { key: "퀵사주_재물운",   label: "재물운",  iconImg: "/woo_y_3.webp", price: 0, color: "#5E8A4E", img: "/woo_04.webp", desc: "나의 타고난 재물그릇은?",                            needPartner: false },
  { key: "퀵사주_건강운",   label: "건강운",  iconImg: "/woo_y_5.webp", price: 0, color: "#5E8A4E", img: "/woo_07.webp", desc: "내 몸의 약한 곳은 어디일까?",                        needPartner: false },
  { key: "퀵사주_신년운세", label: "신년운세", iconImg: "/woo_y_6.webp", price: 0, color: "#4A7BA8", img: "/woo_03.webp", desc: "올해 나의 키워드는?",                                needPartner: false },
  { key: "퀵사주_연애궁합", label: "연애궁합", iconImg: "/woo_y_2.webp", price: 0, color: "#B85C7A", img: "/woo_02.webp", desc: "우리 둘의 궁합점수는?",       needPartner: true, ageLimit: true },
];


const BIRTH_TYPES = [
  { value: "solar",      label: "양력" },
  { value: "lunar",      label: "음력" },
  { value: "lunar_leap", label: "음력 윤달" },
];

/* ── keyframes ──────────────────────────────────────────────── */
const floatQ = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
`;
const dotBounceQ = keyframes`0%,80%,100%{opacity:0.2;transform:scale(0.8)} 40%{opacity:1;transform:scale(1.2)}`;
const shimmerQ   = keyframes`0%{background-position:200% center} 100%{background-position:-200% center}`;

const LoadingOverlayQ = styled.div`
  position: fixed; inset: 0; z-index: 999;
  background: rgba(255,248,240,0.97);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 0;
`;
const LoadingCharQ = styled.img`
  width: 110px; height: 110px; object-fit: contain;
  animation: ${floatQ} 2s ease-in-out infinite;
  filter: drop-shadow(0 8px 24px rgba(200,144,30,0.3));
  margin-bottom: 28px;
`;
const LoadingTitleQ = styled.div`
  font-size: 1.1rem; font-weight: 900; color: #2B2A26;
  margin-bottom: 10px;
`;
const LoadingDotsQ = styled.div`
  display: flex; gap: 7px; margin-bottom: 28px;
  span {
    width: 8px; height: 8px; border-radius: 50%;
    background: #B8862A; display: inline-block;
    animation: ${dotBounceQ} 1.2s ease-in-out infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;
const LoadingProgressQ = styled.div`
  width: 200px; height: 3px; background: #FBF6EA; border-radius: 999px; overflow: hidden;
  &::after {
    content: ''; display: block; height: 100%;
    background: #B8862A;
    background-size: 200% auto;
    border-radius: 999px;
    animation: ${shimmerQ} 1.5s linear infinite;
  }
`;

/* ── styled components ──────────────────────────────────────── */
const DancheongBar = styled.div`
  height: 6px;
  width: 100%;
  background: #FBF6EA;
`;

const Wrap = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 0 0 80px;
  background: #FBF6EA;
`;

const NavBar = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 16px 0;
  margin-bottom: 4px;
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.7);
  border: 1.5px solid #2B2A26;
  border-radius: 20px;
  padding: 7px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  color: #5a4a72;
  font-family: inherit;
  & + & { margin-left: 8px; }
`;

const PageTitle = styled.div`
  font-size: 15px;
  font-weight: 900;
  color: #4a2060;
  margin-left: auto;
`;

/* 전체 씬 히어로 */
const HeroSceneWrap = styled.div`width:100%; max-width:560px; margin:0 auto; text-align:center; padding:26px 20px 8px; background:#FBF6EA;`;
const HeroSceneImg = styled.img`
  width:150px; height:150px; border-radius:50%; object-fit:cover; object-position:center 30%;
  border:2px solid #2B2A26; margin:0 auto 8px; display:block;
  animation:${floatQ} 3.5s ease-in-out infinite;
`;
const HeroSceneOverlay = styled.div`display:none;`;
const HeroTitleBlock = styled.div`text-align:center;`;
const HeroTitle = styled.div`font-size:1.6rem; font-weight:900; color:#2B2A26; margin-bottom:3px;`;
const HeroSub = styled.div`font-size:0.82rem; color:#8C8677; font-weight:600;`;
const SectionLabel = styled.div`font-size:0.9rem; font-weight:900; color:#2B2A26; padding:18px 16px 10px; display:flex; align-items:center; gap:8px; &::after{content:'';flex:1;height:1.5px;background:#EAE3D2;}`;

/* 결과 헤더용 누끼 이미지 */
const ResultHeroImg = styled.img`width:90px; height:90px; object-fit:contain; filter:drop-shadow(0 4px 14px rgba(200,144,30,0.3)); margin-bottom:8px;`;
const SvcIconImg = styled.img`width:32px; height:32px; object-fit:contain; filter:drop-shadow(0 2px 6px rgba(0,0,0,0.15)); margin-bottom:3px;`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── 서비스 카드 — 이미지가 상단에 크게 ─────────────────────── */
const CardGrid = styled.div`display:grid; grid-template-columns:1fr 1fr; gap:14px; padding:0 16px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const SvcCard = styled.button<{ $color: string; $idx: number }>`
  background: #fff;
  border: 2px solid #2B2A26;
  border-radius: 20px; padding: 0;
  cursor: pointer; text-align: left;
  overflow: hidden; font-family: inherit;
  display: flex; flex-direction: column;
  animation: ${fadeUp} 0.35s ease both;
  animation-delay: ${p => p.$idx * 0.07}s;
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-4px); }
`;
const SvcCardImg = styled.div<{ $src: string }>`
  width:100%; height:120px;
  background-image:url(${p => p.$src}); background-size:cover; background-position:center top;
  border-radius:18px 18px 0 0; position:relative; overflow:hidden;
  &::after{content:'';position:absolute;bottom:0;left:0;right:0;height:36px;background:linear-gradient(to bottom,transparent,#fff);}
`;
/* 상단 큰 이미지 배너 */
const SvcImgBanner = styled.img`
  width:100%; height:132px; object-fit:cover; object-position:center 28%;
  border-bottom:2px solid #2B2A26; flex-shrink:0; display:block;
`;
const SvcHeadTitle = styled.div`font-size:1.1rem; font-weight:900; color:#2B2A26; letter-spacing:-0.02em; margin-bottom:6px;`;
const SvcCardBody = styled.div<{ $color: string }>`padding:13px 14px 14px; flex:1; display:flex; flex-direction:column; background:${p => p.$color}22;`;
const SvcDesc = styled.div<{ $color: string }>`font-size:12.5px; font-weight:700; color:#6B6355; line-height:1.55; white-space:pre-line; margin-bottom:10px;`;
/* ── 입력 폼 ──────────────────────────────────────────────────── */
const FormArea = styled.div`padding: 0 16px;`;

const ServiceBadge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${p => p.$color}22;
  border: 2px solid #2B2A26;
  border-radius: 16px;
  padding: 13px 16px;
  margin-bottom: 16px;
`;

const Card = styled.div`
  background: #fff;
  border: 2px solid #2B2A26;
  border-radius: 20px;
  padding: 20px 20px 16px;
  margin-bottom: 14px;
`;

const CardTopBar = styled.div<{ $color: string }>`
  font-size: 13px;
  font-weight: 800;
  color: ${p => p.$color};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 14px;
    background: ${p => p.$color};
    border-radius: 2px;
  }
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #8C8677;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 16px 0 8px;
  &:first-child { margin-top: 0; }
`;

const FieldRow = styled.div`display: flex; gap: 12px; margin-bottom: 12px;`;
const Field = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px;`;
const Label = styled.label`font-size: 12px; color: #8C8677; font-weight: 600;`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1.5px solid #2B2A26;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #2B2A26;
  background: #FBF6EA;
  font-family: inherit;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #B8862A; }
`;

const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1.5px solid #2B2A26;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #2B2A26;
  background: #FBF6EA;
  font-family: inherit;
  &:focus { outline: none; border-color: #B8862A; }
`;

const PartnerDivider = styled.div`
  border-top: 2px dashed #EAE3D2;
  margin: 16px -20px 0;
  padding: 16px 20px 0;
  background: #f8f9fa;
`;

const ConsentBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.8rem;
  color: #8C8677;
  line-height: 1.5;
  margin: 18px 0 0;
  cursor: pointer;
  input { margin-top: 2px; accent-color: #B8862A; width: 16px; height: 16px; flex-shrink: 0; }
`;

const ErrMsg = styled.div`
  padding: 10px 14px;
  background: #fff5f8;
  border: 1px solid #2B2A26;
  border-radius: 10px;
  font-size: 13px;
  color: #c0392b;
  margin-top: 12px;
`;

const SubmitBtn = styled.button<{ $color: string; $disabled?: boolean }>`
  width: 100%;
  padding: 15px;
  background: ${p => p.$disabled ? "#ccc" : p.$color};
  color: #fff;
  border: 2px solid #2B2A26;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 800;
  cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
  font-family: inherit;
  margin-top: 16px;
  transition: opacity 0.2s;
  &:hover { opacity: ${p => p.$disabled ? 1 : 0.92}; }
`;

/* ── 결과 화면 ──────────────────────────────────────────────── */
/* 리포트 보관 안내 — 재발급이 안 되므로 저장을 강하게 안내 */
const SaveNotice = styled.div`
  padding: 13px 15px;
  background: #FFF3BF;
  border: 2px solid #E8590C;
  border-radius: 12px;
  color: #7C2D12;
  @media print { display: none; }
`;
const SaveNoticeTitle = styled.div`
  font-size: .9rem; font-weight: 900; margin-bottom: 4px;
`;
const SaveNoticeBody = styled.div`
  font-size: .78rem; line-height: 1.6;
`;
const SaveNoticeBtn = styled.button`
  margin-top: 10px; width: 100%;
  background: #E8590C; color: #fff;
  border: none; border-radius: 10px;
  padding: 11px 16px; font-size: .85rem; font-weight: 800;
  cursor: pointer; font-family: inherit;
  &:hover { background: #D9480F; }
`;
const ResultCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  margin: 0 16px;
`;

/* ── 인터페이스 ─────────────────────────────────────────────── */
export interface PersonForm {
  name: string;
  gender: string;
  birth_type: string;
  birth_date: string;
  birth_time: string;
  birth_time_unknown: boolean;
  city: string;
}

export const emptyPerson = (): PersonForm => ({
  name: "", gender: "female", birth_type: "solar",
  birth_date: "",
  birth_time: "",
  birth_time_unknown: false,
  city: "서울",
});

/* ── PersonFormBlock (모듈 레벨에 정의 — 성능 핵심) ─────────── */
interface PFBProps {
  title: string;
  data: PersonForm;
  onChange: (d: PersonForm) => void;
  accentColor: string;
  email?: string;
  onEmailChange?: (v: string) => void;
  onSurvey?: () => void;
}

export function PersonFormBlock({ title, data, onChange, accentColor, email, onEmailChange, onSurvey }: PFBProps) {
  const { t } = useLang();
  const birthTypeLabel = (v: string) =>
    v === "solar" ? t("quick.birthTypeSolar")
      : v === "lunar" ? t("quick.birthTypeLunar")
        : t("quick.birthTypeLunarLeap");
  return (
    <Card>
      <CardTopBar $color={accentColor}>{title}</CardTopBar>

      <SectionTitle>{t("quick.basicInfo")}</SectionTitle>
      <FieldRow>
        <Field>
          <Label>{t("quick.nameLabel")}</Label>
          <Input
            placeholder={t("quick.namePlaceholder")}
            value={data.name}
            onChange={e => onChange({ ...data, name: e.target.value })}
            inputMode="text"
            autoComplete="name"
          />
        </Field>
        <Field style={{ maxWidth: 110 }}>
          <Label>{t("quick.genderLabel")}</Label>
          <Select value={data.gender} onChange={e => onChange({ ...data, gender: e.target.value })}>
            <option value="female">{t("quick.genderFemale")}</option>
            <option value="male">{t("quick.genderMale")}</option>
          </Select>
        </Field>
      </FieldRow>

      {onEmailChange !== undefined && (
        <FieldRow>
          <Field>
            <Label>{t("quick.emailLabel")}</Label>
            <Input
              type="email"
              placeholder="example@email.com"
              value={email ?? ""}
              onChange={e => onEmailChange(e.target.value)}
              inputMode="email"
              autoComplete="email"
            />
            <div style={{ fontSize: 11, color: "#9a8aaa", marginTop: 3 }}>
              {t("quick.emailHint")}
            </div>
          </Field>
        </FieldRow>
      )}

      <SectionTitle>{t("quick.birthDateTime")}</SectionTitle>
      <FieldRow>
        <Field>
          <Label>{t("quick.typeLabel")}</Label>
          <Select value={data.birth_type} onChange={e => onChange({ ...data, birth_type: e.target.value })}>
            {BIRTH_TYPES.map(bt => (
              <option key={bt.value} value={bt.value}>{birthTypeLabel(bt.value)}</option>
            ))}
          </Select>
        </Field>
        <Field style={{ flex: 2 }}>
          <Label>{t("quick.birthDateLabel")}</Label>
          <Input
            placeholder="1990/05/15" maxLength={10}
            value={data.birth_date}
            onChange={e => onChange({ ...data, birth_date: e.target.value })}
          />
        </Field>
      </FieldRow>
      <FieldRow style={{ alignItems: "center", marginBottom: 4 }}>
        <Label style={{ marginBottom: 0 }}>{t("quick.birthTimeLabel")}</Label>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#8C8677", cursor: "pointer", marginLeft: 8 }}>
          <input type="checkbox" checked={data.birth_time_unknown}
            onChange={e => onChange({ ...data, birth_time_unknown: e.target.checked })}
            style={{ accentColor: "#B8862A" }} />
          {t("quick.timeUnknown")}
        </label>
        {data.birth_time_unknown && onSurvey && (
          <button type="button" onClick={onSurvey}
            style={{marginLeft:"auto",padding:"4px 10px",border:"1px solid #2B2A26",borderRadius:6,background:"#fff8f0",color:"#B8862A",fontSize:"0.73rem",fontWeight:700,cursor:"pointer"}}>
            {t("quick.estimateHelp")}
          </button>
        )}
      </FieldRow>
      <FieldRow>
        <Field>
          <Input
            placeholder={t("quick.timePlaceholder")} maxLength={5}
            value={data.birth_time_unknown ? "" : data.birth_time}
            disabled={data.birth_time_unknown}
            style={data.birth_time_unknown ? { background: "#FBF6EA", color: "#8C8677", cursor: "not-allowed" } : {}}
            onChange={e => onChange({ ...data, birth_time: e.target.value })}
          />
        </Field>
      </FieldRow>

      <SectionTitle>{t("quick.regionLabel")}</SectionTitle>
      <FieldRow style={{ marginBottom: 0 }}>
        <Field>
          <CityAutocomplete
            value={data.city}
            onChange={v => onChange({ ...data, city: v })}
          />
        </Field>
      </FieldRow>
    </Card>
  );
}

/* ── 결제했는데 결과지를 못 받은 건 기억해두기 ──────────────────
   창을 닫아도, 통신이 끊겨도 남아 있어야 해서 sessionStorage 가 아니라 localStorage 를 쓴다.
   결과지를 손에 넣는 순간 지운다. */
const PAID_PENDING_KEY = "wooyeonQuickPaidPending";

function savePaidPending(v: { id: number; email?: string; lang?: string; impUid?: string; merchantUid?: string }) {
  try { localStorage.setItem(PAID_PENDING_KEY, JSON.stringify({ ...v, at: Date.now() })); } catch { /* noop */ }
}
function loadPaidPending(): any {
  try {
    const v = JSON.parse(localStorage.getItem(PAID_PENDING_KEY) || "null");
    // 열람 기간(7일)이 지난 건은 여기서 다룰 일이 아니다
    if (v && v.at && Date.now() - v.at > 7 * 24 * 3600 * 1000) return null;
    return v;
  } catch { return null; }
}
function clearPaidPending() {
  try { localStorage.removeItem(PAID_PENDING_KEY); } catch { /* noop */ }
}

/* ── 결제창에서 돌아온 주소 읽기 ──────────────────────────────────
   결제 회사는 돌아올 주소 뒤에 결제번호를 붙여서 보낸다. 그런데 돌아올 주소에
   이미 물음표가 있으면 물음표가 두 번 붙어(`/quick?unlock=1?imp_uid=…`)
   결제번호를 통째로 못 읽는 일이 생긴다. 그러면 결제는 됐는데 서버는
   "결제 정보가 없습니다"로 막는다 — 돈만 나가고 결과가 안 나오는 바로 그 사고다.
   그래서 ① 돌아올 주소에는 물음표를 달지 않고, ② 여기서 두 번째 물음표를
   `&`로 바꿔서 읽는다(예전 주소로 돌아오는 분들까지 구제). */
function payParams(): URLSearchParams {
  const raw = (window.location.search || "").replace(
    /\?/g, (_m, i) => (i === 0 ? "?" : "&"),
  );
  return new URLSearchParams(raw);
}

/** 이 화면이 지금 무엇을 이어받아야 하는지 한 번만 판단한다.
    'unlock' = 전체 결과지 결제 복귀 · 'legacy' = 무료화 전 쿠키 결제 복귀 */
function readPayReturn() {
  const q = payParams();
  const impUid = q.get("imp_uid") || "";
  const merchantUid = q.get("merchant_uid") || "";
  const success = q.get("imp_success") ?? q.get("success");
  // 결제창에서 돌아온 게 맞는지 — 결제번호나 성공여부가 있어야 한다.
  // (결제창을 그냥 닫으면 주소에 아무것도 안 붙는다)
  const isReturn = !!(impUid || success !== null || q.get("unlock"));
  const rawUnlock = sessionStorage.getItem("quickUnlock");
  const rawLegacy = sessionStorage.getItem("quickPending");
  let kind: "unlock" | "legacy" | null = null;
  if (isReturn) kind = rawUnlock ? "unlock" : (rawLegacy ? "legacy" : null);
  if (!isReturn && rawUnlock) {
    try { sessionStorage.removeItem("quickUnlock"); } catch { /* noop */ }
  }
  let saved: any = null;
  try { saved = JSON.parse((kind === "unlock" ? rawUnlock : rawLegacy) || "null"); } catch { /* noop */ }
  return { kind, impUid, merchantUid, success, saved, isReturn };
}

/* ── QuickPage ───────────────────────────────────────────────── */
export default function QuickPage() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [step, setStep]     = useState<0 | 1>(0);
  const [svcKey, setSvcKey] = useState<string>("");
  const [person1, setPerson1] = useState<PersonForm>(emptyPerson());
  const [person2, setPerson2] = useState<PersonForm>(emptyPerson());
  const [email, setEmail]   = useState<string>("");
  const [consent, setConsent] = useState(false);
  const [err, setErr]       = useState<string>("");
  const [paying, setPaying]   = useState(false);
  const [previewing] = useState(false);   // 무료화 이후 미리보기 단계 없음
  const [upBusy, setUpBusy] = useState(false);   // 전체 결과지 결제 중
  const [upErr,  setUpErr]  = useState("");
  /* 결제는 끝났는데 결과지를 아직 손에 넣지 못한 건.
     여기에 값이 있으면 화면은 '돈은 정상 처리됐고 다시 받아올 수 있다'고 크게 알린다. */
  const [paidPending, setPaidPending] = useState<{ id: number } | null>(null);
  /* 결제창에서 돌아온 참인지 — 주소는 곧 정리되므로 그리기 단계에서 한 번만 읽어 붙잡아 둔다.
     아래 세 곳(전체결과지 복귀·예전 결제 복귀·못 받은 결과 되찾기)이 이 판단 하나를 나눠 쓴다.
     useState 의 게으른 초기화를 쓰는 이유: useRef(읽기())로 하면 그릴 때마다 다시 읽혀서
     저장해 둔 결제 기록을 엉뚱한 때 지워버린다. */
  const [payReturn] = useState(readPayReturn);
  const [result, setResult]   = useState<{
    emailSent: boolean;
    reportHtml: string;
    lockedSections?: string[];   // 결제하면 열리는 꼭지 제목들
    lockedItems?: { title: string; desc: string }[];   // 제목 + 그 안에 뭐가 있는지
    hookHead?: string;           // 결제로 잇는 문구(서버 생성 — 마스킹한 연도가 들어간다)
    hookBody?: string;
    upsellService?: string;      // 이어지는 우연 사주 상품명
    upsellPrice?: number;
    unlocked?: boolean;          // 결제해서 전체 결과지로 바뀐 상태
    consultationId: number | null;
    wx?: any;
    dayStem?: string;
    partnerWx?: any;
    partnerDayStem?: string;
  } | null>(null);
  /* ─── 시주 추정 설문 state ─── */
  const [surveying,     setSurveying]     = useState<"p1"|"p2"|null>(null);
  const [surveyQs,      setSurveyQs]      = useState<{id:number;question:string;choices:string[]}[]>([]);
  const [surveyLang,    setSurveyLang]    = useState<string>("");
  const [surveyAns,     setSurveyAns]     = useState<number[]>(Array(10).fill(-1));
  const [surveyResults, setSurveyResults] = useState<any[]>([]);

  const openSurveyFor = async (target: "p1"|"p2") => {
    // 설문 문항은 언어별로 다르므로 언어가 바뀌면 다시 받아온다
    if (surveyQs.length === 0 || surveyLang !== lang) {
      try {
        const r = await axios.get(`${API_BASE}/api/v1/saju/hour-survey`, { params: { lang } });
        setSurveyQs(r.data.questions);
        setSurveyLang(lang);
      } catch { return; }
    }
    setSurveyAns(Array(10).fill(-1));
    setSurveyResults([]);
    setSurveying(target);
  };
  const closeSurvey = () => setSurveying(null);
  const submitSurvey = async () => {
    if (surveyAns.some(a => a < 0)) return;
    try {
      const r = await axios.post(`${API_BASE}/api/v1/saju/estimate-hour`, { answers: surveyAns, lang });
      setSurveyResults(r.data.recommendations);
    } catch {}
  };
  const applySurveyResult = (birthHour: number) => {
    const timeStr = String(birthHour).padStart(2,"0") + ":00";
    if (surveying === "p1") setPerson1(p => ({ ...p, birth_time: timeStr, birth_time_unknown: false }));
    else if (surveying === "p2") setPerson2(p => ({ ...p, birth_time: timeStr, birth_time_unknown: false }));
    setSurveying(null);
  };

  const svc = SERVICES.find(s => s.key === svcKey);

  /* 서비스 표시명/설명 — 표시만 번역(내부 key는 한국어 유지) */
  const svcLabel = (key: string): string => {
    switch (key) {
      case "퀵사주_연애운":   return t("svc.연애운");
      case "퀵사주_직장운":   return t("quick.svcCareer");
      case "퀵사주_재물운":   return t("quick.svcWealth");
      case "퀵사주_건강운":   return t("svc.건강운");
      case "퀵사주_신년운세": return t("svc.신년운세");
      case "퀵사주_연애궁합": return t("svc.연애궁합");
      // 우연 사주 상품명(연애운·직업운 등)이 그대로 들어오는 경우 — 이어보기 버튼용
      default: return key ? t(`svc.${key}`) : "";
    }
  };
  /* 카드 설명 문구. 상품을 새로 만들 때 여기에 한 줄 추가하는 걸 잊으면
     그 카드만 설명이 통째로 비어 보인다(건강운·신년운세가 그랬다).
     빠뜨려도 카드가 비지 않도록 마지막에 SERVICES 의 desc 로 받쳐준다. */
  const svcDesc = (key: string): string => {
    switch (key) {
      case "퀵사주_연애운":   return t("quick.svcLoveDesc");
      case "퀵사주_직장운":   return t("quick.svcCareerDesc");
      case "퀵사주_재물운":   return t("quick.svcWealthDesc");
      case "퀵사주_건강운":   return t("quick.svcHealthDesc");
      case "퀵사주_신년운세": return t("quick.svcNewYearDesc");
      case "퀵사주_연애궁합": return t("quick.svcCompatDesc");
      default: return SERVICES.find(s => s.key === key)?.desc || "";
    }
  };

  function selectService(key: string) {
    setSvcKey(key);
    setPerson1(emptyPerson());
    setPerson2(emptyPerson());
    setEmail("");
    setConsent(false);
    setErr("");
    setResult(null);
    setStep(1);
  }

  function validateForm(): string | null {
    if (!consent) return t("quick.errConsent");
    const p = person1;
    if (!p.name.trim()) return t("quick.errName");
    const { year: yr, month: mo, day: dy } = parseBirthDate(p.birth_date);
    if (!yr || yr < 1900 || yr > 2020) return t("quick.errBirthDate");
    if (!mo || mo < 1 || mo > 12) return t("quick.errBirthMonth");
    if (!dy || dy < 1 || dy > 31) return t("quick.errBirthDay");
    if (!email.trim()) return t("quick.errEmailEmpty");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return t("quick.errEmailInvalid");
    if (svc?.needPartner) {
      const p2 = person2;
      if (!p2.name.trim()) return t("quick.errPartnerName");
      const { year: yr2, month: mo2, day: dy2 } = parseBirthDate(p2.birth_date);
      if (!yr2 || yr2 < 1900 || yr2 > 2020) return t("quick.errPartnerBirthDate");
      if (!mo2 || mo2 < 1 || mo2 > 12) return t("quick.errPartnerBirthMonth");
      if (!dy2 || dy2 < 1 || dy2 > 31) return t("quick.errPartnerBirthDay");
      if (svc?.ageLimit) {
        const now = new Date().getFullYear();
        if (now - yr < 20 || now - yr2 < 20) return t("quick.errAgeLimit");
      }
    }
    return null;
  }

  async function callAnalyze(impUid?: string, merchantUid?: string) {
    setPaying(true);
    try {
      const p1d = parseBirthDate(person1.birth_date);
      const body: any = {
        service_type: svcKey,
        lang,
        email: email.trim(),
        imp_uid: impUid,
        merchant_uid: merchantUid,
        person1: {
          name: person1.name,
          gender: person1.gender,
          birth_year:   p1d.year,
          birth_month:  p1d.month,
          birth_day:    p1d.day,
          birth_hour:   person1.birth_time_unknown ? 99 : parseTime(person1.birth_time).hour,
          birth_minute: person1.birth_time_unknown ? 0 : parseTime(person1.birth_time).minute,
          birth_type:   person1.birth_type,
          city:         person1.city,
        },
      };
      if (svc?.needPartner) {
        const p2d = parseBirthDate(person2.birth_date);
        body.person2 = {
          name: person2.name,
          gender: person2.gender,
          birth_year:   p2d.year,
          birth_month:  p2d.month,
          birth_day:    p2d.day,
          birth_hour:   person2.birth_time_unknown ? 99 : parseTime(person2.birth_time).hour,
          birth_minute: person2.birth_time_unknown ? 0 : parseTime(person2.birth_time).minute,
          birth_type:   person2.birth_type,
          city:         person2.city,
        };
      }
      const res = await axios.post(`${API_BASE}/api/v1/quick/analyze`, body);
      setResult({
        emailSent:      res.data.email_sent === true,
        reportHtml:     res.data.report_html || "",
        lockedSections: res.data.locked_sections || [],
        lockedItems:    res.data.locked_items || [],
        hookHead:       (res.data.upsell_hook || {}).headline || "",
        hookBody:       (res.data.upsell_hook || {}).body || "",
        upsellService:  res.data.upsell_service || "",
        upsellPrice:    res.data.upsell_price || 0,
        consultationId: res.data.consultation_id ?? null,
        wx:             res.data.wuxing ?? null,
        dayStem:        res.data.day_stem || "",
        partnerWx:      res.data.partner_wuxing ?? null,
        partnerDayStem: res.data.partner_day_stem || "",
      });
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t("quick.errAnalyze"));
      setPaying(false);
    }
  }

  /* 휴대폰으로 전체 결과지를 결제하고 돌아왔을 때 — 접수번호로 이어서 발급한다.
     휴대폰 결제는 콜백이 아니라 주소 이동으로 돌아오기 때문에 화면 상태가 날아간다. */
  React.useEffect(() => {
    if (payReturn.kind !== "unlock") return;
    const { impUid, merchantUid, success, saved } = payReturn;
    sessionStorage.removeItem("quickUnlock");
    window.history.replaceState({}, "", "/quick");

    if (success === "false" || success === "0") { setUpErr(t("quick.moreFailed")); return; }
    if (!saved?.consultationId) return;

    // 결제까지는 끝난 상태로 돌아온 것이므로 먼저 적어둔다 — 여기서 끊겨도 되찾을 수 있게
    savePaidPending({
      id: saved.consultationId, email: saved.email, lang: saved.lang,
      impUid, merchantUid: merchantUid || saved.merchantUid,
    });
    setPaidPending({ id: saved.consultationId });

    setUpBusy(true);
    (async () => {
      try {
        const res = await axios.post(`${API_BASE}/api/v1/quick/upgrade`, {
          consultation_id: saved.consultationId,
          imp_uid: impUid, merchant_uid: merchantUid || saved.merchantUid,
          email: (saved.email || "").trim(), lang: saved.lang || "ko",
        });
        if (!res.data?.report_html) throw new Error("빈 결과지");
        clearPaidPending();
        setPaidPending(null);
        setEmail(saved.email || "");
        setResult({
          emailSent: res.data.email_sent === true,
          reportHtml: res.data.report_html || "",
          lockedSections: [], unlocked: true,
          consultationId: saved.consultationId,
        });
      } catch (e: any) {
        setUpErr(e?.response?.data?.detail || t("quick.errAnalyze"));
      } finally { setUpBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 예전 쿠키 결제 흐름의 복귀 처리 — 무료화 전에 결제한 건이 돌아올 수 있어 남겨둔다 */
  React.useEffect(() => {
    if (payReturn.kind !== "legacy") return;
    const { impUid, merchantUid, success, saved } = payReturn;
    sessionStorage.removeItem("quickPending");
    window.history.replaceState({}, "", "/quick");   // 주소창의 결제 파라미터 정리

    if (success === "false" || success === "0") {
      setErr(t("quick.payFail", { msg: payParams().get("error_msg") || t("quick.payRetry") }));
      return;
    }
    if (!saved) {
      setErr(t("quick.payRetry"));
      return;
    }
    // 저장해 둔 입력값 복원 후 분석 요청
    setSvcKey(saved.svcKey);
    setEmail(saved.email || "");
    if (saved.person1) setPerson1(saved.person1);
    if (saved.person2) setPerson2(saved.person2);
    setStep(1);
    setPaying(true);
    (async () => {
      try {
        const res = await axios.post(`${API_BASE}/api/v1/quick/analyze`, {
          service_type: saved.svcKey,
          lang: saved.lang || "ko",
          email: (saved.email || "").trim(),
          imp_uid: impUid,
          merchant_uid: merchantUid || saved.merchantUid,
          person1: toPersonPayload(saved.person1),
          ...(saved.person2 && SERVICES.find(x => x.key === saved.svcKey)?.needPartner
              ? { person2: toPersonPayload(saved.person2) } : {}),
        });
        setResult({
          emailSent:      res.data.email_sent === true,
          reportHtml:     res.data.report_html || "",
          consultationId: res.data.consultation_id ?? null,
          wx:             res.data.wuxing ?? null,
          dayStem:        res.data.day_stem || "",
          partnerWx:      res.data.partner_wuxing ?? null,
          partnerDayStem: res.data.partner_day_stem || "",
        });
      } catch (e: any) {
        setErr(e?.response?.data?.detail || t("quick.errAnalyze"));
        setPaying(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 무료화 이후 — 미리보기 단계 없이 바로 결과로 간다. */
  async function handlePreview() {
    setErr("");
    const e = validateForm();
    if (e) { setErr(e); return; }
    await callAnalyze();
  }

  /* 무료 결과 화면에서 그 자리로 전체 결과지를 결제한다.
     화면을 옮기지 않는다 — 입력폼으로 되돌아가면 대부분 거기서 빠져나간다. */
  async function unlockFull(impUid?: string, merchantUid?: string, cid?: number,
                            emailOverride?: string) {
    const consultationId = cid ?? result?.consultationId;
    if (!consultationId) return;
    setUpErr("");
    setUpBusy(true);
    try {
      const res = await axios.post(`${API_BASE}/api/v1/quick/upgrade`, {
        consultation_id: consultationId,
        imp_uid: impUid, merchant_uid: merchantUid,
        // 되찾기 때는 화면 상태가 비어 있을 수 있어 적어둔 주소를 그대로 쓴다
        email: (emailOverride ?? email).trim(), lang,
      });
      if (!res.data?.report_html) throw new Error("빈 결과지");
      // 결과지를 손에 넣었다 — 이제 미결제 표시를 지운다
      clearPaidPending();
      setPaidPending(null);
      setResult(r => ({
        ...(r || { consultationId, emailSent: false, reportHtml: "" }),
        consultationId,
        reportHtml: res.data.report_html,
        lockedSections: [],          // 이제 잠긴 곳이 없다
        lockedItems: [],
        unlocked: true,
        emailSent: res.data.email_sent === true || !!r?.emailSent,
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e: any) {
      // 여기까지 왔다는 건 결제창은 성공하고 발급만 실패했다는 뜻이다.
      // 접수번호를 남겨두고 크게 알린다 — 작은 빨간 글씨로 넘어가면 안 되는 상황.
      savePaidPending({ id: consultationId, email: email.trim(), lang, impUid, merchantUid });
      setPaidPending({ id: consultationId });
      setUpErr(e?.response?.data?.detail || t("quick.errAnalyze"));
    } finally {
      setUpBusy(false);
    }
  }

  /* 결제는 됐는데 결과를 못 받은 건을 다시 받아온다.
     결제 확인은 서버가 이미 끝냈으므로 접수번호만 있으면 된다. */
  async function retryPaid(cid: number) {
    const saved = loadPaidPending() || {};
    setUpErr("");
    setUpBusy(true);
    try {
      const got = await axios.get(`${API_BASE}/api/v1/quick/upgrade/result/${cid}`);
      if (got.data?.ready && got.data.report_html) {
        clearPaidPending();
        setPaidPending(null);
        setResult(r => ({
          ...(r || { emailSent: false, reportHtml: "" }),
          consultationId: cid,
          reportHtml: got.data.report_html,
          lockedSections: [], lockedItems: [], unlocked: true,
          emailSent: !!r?.emailSent,
        }));
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      // 결제는 됐는데 결과지가 아직 없다 → 다시 만들어 달라고 한다
      await unlockFull(saved.impUid, saved.merchantUid, cid, saved.email);
    } catch (e: any) {
      setUpErr(e?.response?.data?.detail || t("quick.errAnalyze"));
    } finally {
      setUpBusy(false);
    }
  }

  /* 지난번에 결제까지 하고 결과를 못 받은 게 있으면 화면을 열자마자 되찾아 온다 */
  React.useEffect(() => {
    // 결제창에서 막 돌아온 참이면 위쪽 처리가 이미 맡고 있다 — 두 번 부르지 않는다
    if (payReturn.kind) return;
    const p = loadPaidPending();
    if (!p?.id) return;
    setPaidPending({ id: p.id });
    if (p.email) setEmail(p.email);
    retryPaid(p.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleUnlockPay() {
    if (!result?.consultationId) return;
    setUpErr("");
    const cid = result.consultationId;

    /* 결제창을 열기 전에 서버가 이 접수를 발급할 수 있는지 먼저 묻는다.
       화면만 새로 올라가고 서버가 옛 버전이면 이 물음에서 걸린다 —
       그 상태로 결제부터 하면 돈만 빠져나가고 결과가 안 나온다. */
    setUpBusy(true);
    try {
      const info = await axios.get(`${API_BASE}/api/v1/quick/upgrade/info/${cid}`, { timeout: 15000 });
      if (!info.data?.ok) throw new Error("not ok");
      if (info.data.report_ready) {          // 이미 결제된 건이면 결제 없이 바로 돌려준다
        await retryPaid(cid);
        return;
      }
    } catch {
      setUpBusy(false);
      setUpErr(t("quick.payNotReady"));
      return;
    }

    const IMP = (window as any).IMP;
    if (!IMP) { unlockFull(); return; }   // 결제 모듈 없으면 바로(개발용)

    const merchantUid = `wooyeon_${cid}_${Date.now()}`;
    // 휴대폰 결제는 콜백 대신 주소 이동으로 돌아온다 → 접수번호를 남겨두고 복귀 시 이어서 처리
    try {
      sessionStorage.setItem("quickUnlock", JSON.stringify({
        consultationId: cid, email, lang, merchantUid,
      }));
    } catch { /* 저장 실패해도 PC 결제는 정상 동작 */ }
    requestPay({
      merchantUid,
      name: t("quick.payName", { svc: svcLabel(result.upsellService || "") }),
      amount: result.upsellPrice || 0,
      buyerName: person1.name,
      buyerEmail: email.trim(),
      // 돌아올 주소에 물음표를 달지 않는다 — 결제번호가 물음표로 또 붙으면
      // 주소가 깨져서 결제번호를 못 읽는다. 무슨 결제였는지는 sessionStorage 로 안다.
      redirectUrl: `${window.location.origin}/quick`,
    }).then((rsp) => {
      if (rsp.success) {
        // 결제창이 성공을 알린 시점에 먼저 적어둔다 — 이 다음 단계에서 끊겨도 되찾을 수 있게.
        savePaidPending({ id: cid, email: email.trim(), lang, impUid: rsp.imp_uid, merchantUid: rsp.merchant_uid });
        unlockFull(rsp.imp_uid, rsp.merchant_uid, cid);
      } else { setUpBusy(false); setUpErr(t("quick.moreFailed")); }
    });
  }


  const accentColor = svc?.color || "#7048a8";

  /* 결제는 됐는데 결과지를 못 받은 상태 안내.
     돈이 빠져나간 화면이라 작게 쓰면 안 된다 — 무슨 일이 있었고 어떻게 되찾는지, 접수번호까지 크게. */
  const paidPendingBox = paidPending && (
    <div style={{
      background: "#FFF7E6", border: "2px solid #B8862A", borderRadius: 18,
      padding: "20px 18px", marginBottom: 16, textAlign: "left",
    }}>
      <div style={{ fontSize: "1rem", fontWeight: 900, color: "#7a4f2d", marginBottom: 8 }}>
        {t("quick.paidTitle")}
      </div>
      <div style={{
        fontSize: "0.86rem", color: "#5a4535", lineHeight: 1.7,
        whiteSpace: "pre-line", marginBottom: 14, wordBreak: "keep-all",
      }}>
        {t("quick.paidBody", { id: String(paidPending.id) })}
      </div>
      <button
        onClick={() => retryPaid(paidPending.id)}
        disabled={upBusy}
        style={{
          width: "100%", padding: "15px", border: "none", borderRadius: 50,
          background: upBusy ? "#C9BFA8" : "#B8862A", color: "#fff",
          fontSize: "0.98rem", fontWeight: 900, fontFamily: "inherit",
          cursor: upBusy ? "default" : "pointer",
        }}>
        {upBusy ? t("quick.paidRetrying") : t("quick.paidRetry")}
      </button>
      <div style={{ fontSize: "0.74rem", color: "#8a7560", marginTop: 10, textAlign: "center" }}>
        {t("quick.paidContact", { id: String(paidPending.id) })}
      </div>
    </div>
  );

  /* 무료 결과 아래에 붙일 후킹 문구 — 상품마다 '실제로 뭐가 남았는지'를 콕 집는다.
     무료 구간에 이미 있는 내용을 안 보인다고 쓰면 거짓말이 되므로,
     문구는 실제 절단 위치에 맞춰 i18n에 적어뒀다. */


  /* ── 분석/결제 로딩 ─────────────────────────────────────── */
  if (previewing || (paying && !result)) {
    const msg = previewing ? t("quick.loadingAnalyze") : t("quick.loadingReport");
    return (
      <>
        <Global />
        <LoadingOverlayQ>
          <LoadingCharQ src="/woo_y_1.webp" alt={t("quick.altAnalyzing")} />
          <LoadingTitleQ>{msg}</LoadingTitleQ>
          <LoadingDotsQ><span /><span /><span /></LoadingDotsQ>
          <LoadingProgressQ />
          <div style={{ fontSize: "0.75rem", color: "#8C8677", marginTop: 16 }}>
            {t("quick.loadingSub")}
          </div>
        </LoadingOverlayQ>
      </>
    );
  }

  /* ── 미리보기 화면 ───────────────────────────────────────── */

  /* ── 결과 화면 ───────────────────────────────────────────── */
  if (result) {
    return (
      <>
        <Global />
        <DancheongBar />
        <Wrap>
          <NavBar>
            <NavBtn onClick={() => navigate("/")}>{t("quick.home")}</NavBtn>
            <PageTitle>{t("quick.brand")}</PageTitle>
          </NavBar>

          <div style={{ textAlign: "center", padding: "28px 16px 8px" }}>
            <ResultHeroImg src="/woo_y_1.webp" alt={t("quick.altComplete")} />
            <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#2B2A26" }}>{t("quick.analyzeComplete")}</div>
          </div>

          <ResultCard>
            {paidPendingBox}
            {/* 결과는 재발급 안 됨 — 결과 화면 맨 위에 저장 안내 */}
            <SaveNotice style={{ margin: "0 0 16px", textAlign: "left" }}>
              <SaveNoticeTitle>⚠️ {t("common.saveWarnTitle")}</SaveNoticeTitle>
              <SaveNoticeBody>{t("common.saveWarnAfter")}</SaveNoticeBody>
              <SaveNoticeBtn onClick={() => saveReport(result.reportHtml, `쿠키사주_${person1.name || "결과"}`)}>{t("common.saveWarnBtn")}</SaveNoticeBtn>
          {isInAppBrowser() && (
            <div style={{ marginTop: 8, fontSize: ".75rem", lineHeight: 1.5, color: "#7C2D12" }}>
              {t("common.saveWarnInApp")}
            </div>
          )}
            </SaveNotice>

            {result.emailSent && (
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: `${accentColor}12`, border: `1.5px solid ${accentColor}35`,
                borderRadius: 14, padding: "12px 16px", marginBottom: 16, textAlign: "left",
              }}>
                <span style={{ fontSize: "1.3rem" }}>📩</span>
                <div style={{ fontSize: 13, color: "#2B2A26", lineHeight: 1.6 }}>
                  {t("quick.emailSentPrefix")}<strong style={{ color: accentColor }}>{email}</strong>{t("quick.emailSentSuffix")}<br />
                  <span style={{ fontSize: 11, color: "#9a8aaa" }}>{t("quick.checkSpam")}</span>
                </div>
              </div>
            )}

            {/* 오행 다이어그램은 리포트 본문(기본정보 바로 아래)에 들어 있다 — 여기서 따로 그리지 않음 */}
            {/* 서비스 소개('○○운 이야기') 카드는 결과 화면에 불필요해서 제거 (2026-08-03) */}

            {result.reportHtml ? (
              <iframe
                srcDoc={result.reportHtml}
                title={t("quick.resultFrame")}
                style={{
                  width: "100%", border: "none", borderRadius: 16,
                  minHeight: 700, display: "block",
                  boxShadow: "none",
                  marginBottom: 16,
                }}
                onLoad={e => {
                  const iframe = e.currentTarget;
                  try {
                    const h = iframe.contentDocument?.documentElement?.scrollHeight;
                    if (h && h > 100) iframe.style.height = h + 40 + "px";
                  } catch { /* cross-origin 무시 */ }
                }}
              />
            ) : (
              <div style={{ fontSize: 15, fontWeight: 800, color: "#2B2A26", padding: "24px 0" }}>
                {t("quick.analyzeDoneTitle")}<br />
                <span style={{ fontSize: 13, fontWeight: 400, color: "#7a6a8a" }}>
                  {t("quick.resultEmailed")}
                </span>
              </div>
            )}

            {/* ── 여기서부터는 우연 사주 ─────────────────────────
                무료 구간에서 잘린 꼭지들을 제목만 보여주고, 이어서 볼 수 있게 한다.
                입력값을 그대로 들고 넘어가므로 생년월일을 다시 쓰지 않아도 된다. */}
            {(result.lockedSections || []).length > 0 && (
              <div style={{
                background: "#2B2A26",          // 크림 배경에서 확 튀도록 어둡게
                borderRadius: 22, padding: "26px 20px 22px",
                marginBottom: 16, textAlign: "left", color: "#FBF6EA",
                boxShadow: "0 10px 30px rgba(43,42,38,0.22)",
              }}>
                <div style={{
                  display: "inline-block",
                  fontSize: "0.68rem", fontWeight: 900, letterSpacing: 1.2,
                  color: "#2B2A26", background: accentColor,
                  padding: "5px 12px", borderRadius: 999, marginBottom: 14,
                }}>
                  {t("quick.moreLabel")}
                </div>

                {/* 상품별 후킹 문구 — 실제로 뭐가 남았는지 콕 집어 말한다 */}
                <div style={{
                  fontSize: "1.18rem", fontWeight: 900, lineHeight: 1.5,
                  color: "#FBF6EA", marginBottom: 12, wordBreak: "keep-all",
                }}>
                  {result.hookHead || t("quick.moreTitleFallback")}
                </div>
                <div
                  style={{
                    fontSize: "0.95rem", fontWeight: 500, lineHeight: 1.8,
                    color: "rgba(251,246,234,0.82)", marginBottom: 20,
                    wordBreak: "keep-all", whiteSpace: "pre-line",
                  }}
                  dangerouslySetInnerHTML={{ __html: result.hookBody || "" }}
                />

                {/* 잠긴 꼭지는 작은 알약으로 — 목록이 주인공이 되면 지루해진다 */}
                {/* 제목만 나열하면 아무것도 안 궁금하다 — 그 안에 뭐가 있는지를 같이 적는다 */}
                <div style={{ display: "grid", gap: 9, marginBottom: 20 }}>
                  {(result.lockedItems || []).map((it, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "flex-start", gap: 9,
                      background: "rgba(251,246,234,0.07)",
                      border: "1px solid rgba(251,246,234,0.16)",
                      borderRadius: 13, padding: "12px 14px",
                    }}>
                      <span style={{ opacity: 0.55, fontSize: "0.82rem", lineHeight: 1.5 }}>🔒</span>
                      <div>
                        <div style={{ fontSize: "0.9rem", fontWeight: 800, color: "#FBF6EA" }}>
                          {it.title}
                        </div>
                        {it.desc && (
                          <div style={{
                            fontSize: "0.79rem", color: "rgba(251,246,234,0.62)",
                            marginTop: 3, lineHeight: 1.6, wordBreak: "keep-all",
                          }}>{it.desc}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* 결제도 이 화면에서 끝난다 — 입력폼으로 돌려보내지 않는다 */}
                <button
                  onClick={handleUnlockPay}
                  disabled={upBusy}
                  style={{
                    width: "100%", padding: "17px",
                    background: upBusy ? "#6B665C" : accentColor,
                    border: "none", borderRadius: 50,
                    fontSize: "1.02rem", fontWeight: 900, color: "#fff",
                    cursor: upBusy ? "default" : "pointer",
                    fontFamily: "inherit", letterSpacing: "0.3px",
                    boxShadow: upBusy ? "none" : "0 6px 18px rgba(0,0,0,0.28)",
                  }}>
                  {upBusy
                    ? t("quick.morePaying")
                    : t("quick.moreBtn", {
                        svc: svcLabel(result.upsellService || ""),
                        price: (result.upsellPrice || 0).toLocaleString(),
                      })}
                </button>
                {upErr && (
                  <div style={{ color: "#FFA8A8", fontSize: "0.8rem", marginTop: 10, fontWeight: 700, textAlign: "center" }}>
                    {upErr}
                  </div>
                )}
                <div style={{ fontSize: "0.72rem", color: "rgba(251,246,234,0.62)", marginTop: 10, textAlign: "center", lineHeight: 1.6 }}>
                  {t("quick.moreVolume")}<br />
                  {t("quick.moreNote")} {t("quick.moreBtnSub")}
                </div>
                <div style={{ fontSize: "0.68rem", marginTop: 8, textAlign: "center" }}>
                  <a href="/legal/refund" target="_blank" rel="noreferrer"
                    style={{ color: "rgba(251,246,234,0.45)", textDecoration: "underline" }}>
                    {t("quick.refundPolicy")}
                  </a>
                </div>
              </div>
            )}

            {/* 결과지가 길어서 아래 결제 버튼까지 내려오기 전에 대부분 이탈한다.
                화면 맨 아래에 상품 이름을 단 버튼을 붙여 어디서든 이어갈 수 있게 한다.
                (결과지는 iframe 안이라 그 안에서 fixed 를 쓰면 화면에 안 붙는다) */}
            {!result.unlocked && (result.lockedSections || []).length > 0 && (
              <div style={{ height: 78 }} />
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => navigate("/")}
                style={{
                  flex: 1, padding: "13px",
                  background: "#fff", border: "1.5px solid #2B2A26",
                  borderRadius: 14, fontSize: 14, fontWeight: 700,
                  color: "#5a4a72", cursor: "pointer", fontFamily: "inherit",
                }}>
                {t("quick.goMain")}
              </button>
              <button
                onClick={() => window.location.href = "/quick"}
                style={{
                  flex: 1, padding: "13px",
                  background: accentColor,
                  border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800,
                  color: "#fff", cursor: "pointer", fontFamily: "inherit",
                }}>
                {t("quick.seeCookieSaju")}
              </button>
            </div>
          </ResultCard>
          <SiteFooter />
        </Wrap>

        {/* ── 화면 하단 고정 결제 버튼 ─────────────────────────────
            상품 이름을 그대로 달아서, 지금 무엇을 사는지 한눈에 보이게 한다. */}
        {!result.unlocked && (result.lockedSections || []).length > 0 && (
          <div style={{
            position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 60,
            background: "rgba(43,42,38,0.96)",
            borderTop: "1.5px solid rgba(251,246,234,0.18)",
            padding: "10px 14px calc(10px + env(safe-area-inset-bottom))",
          }}>
            <div style={{ maxWidth: 560, margin: "0 auto", display: "flex",
                          alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: "#FBF6EA",
                              whiteSpace: "nowrap", overflow: "hidden",
                              textOverflow: "ellipsis" }}>
                  {svcLabel(result.upsellService || "")}
                </div>
                <div style={{ fontSize: 11, color: "rgba(251,246,234,0.6)", marginTop: 2 }}>
                  {t("quick.moreCount", { n: (result.lockedSections || []).length })}
                </div>
              </div>
              <button
                onClick={handleUnlockPay}
                disabled={upBusy}
                style={{
                  flexShrink: 0, padding: "13px 20px",
                  background: upBusy ? "#6B665C" : accentColor,
                  border: "none", borderRadius: 50,
                  fontSize: 14, fontWeight: 900, color: "#fff",
                  cursor: upBusy ? "default" : "pointer", fontFamily: "inherit",
                }}>
                {upBusy ? t("quick.morePaying")
                        : t("quick.moreBtn", {
                            svc: svcLabel(result.upsellService || ""),
                            price: (result.upsellPrice || 0).toLocaleString(),
                          })}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <Global />
      {/* ── 시주 추정 설문 모달 ─────────────────────────── */}
      {surveying && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
          onClick={e => { if (e.target === e.currentTarget) closeSurvey(); }}>
          <div style={{background:"#fff",borderRadius:18,width:"100%",maxWidth:520,maxHeight:"88vh",overflowY:"auto",padding:"26px 22px",boxShadow: "none"}}>
            <div style={{fontSize:"1rem",fontWeight:800,color:"#2B2A26",marginBottom:4}}>{t("quick.surveyTitle")}</div>
            <div style={{fontSize:"0.78rem",color:"#9a7d6a",marginBottom:18}}>
              {t("quick.surveyDesc")}{surveying==="p2"?t("quick.surveyForPartner"):""}
            </div>
            {surveyResults.length === 0 ? (
              <>
                {surveyQs.map((q, qi) => (
                  <div key={q.id} style={{marginBottom:18}}>
                    <div style={{fontSize:"0.83rem",fontWeight:700,color:"#2B2A26",marginBottom:7}}>{qi+1}. {q.question}</div>
                    {q.choices.map((c, ci) => (
                      <label key={ci} onClick={() => setSurveyAns(a => { const n=[...a]; n[qi]=ci; return n; })}
                        style={{display:"flex",alignItems:"flex-start",gap:8,padding:"8px 12px",borderRadius:8,cursor:"pointer",fontSize:"0.8rem",marginBottom:5,
                          background:surveyAns[qi]===ci?"#FFF3E0":"#F8F4F0",
                          border:`1.5px solid ${surveyAns[qi]===ci?"#B8862A":"#e8dfd5"}`,
                          color:surveyAns[qi]===ci?"#7a4f2d":"#5a4535",transition:"all 0.15s"}}>
                        <input type="radio" readOnly checked={surveyAns[qi]===ci} style={{marginTop:2,accentColor:"#B8862A",flexShrink:0}} />
                        {c}
                      </label>
                    ))}
                  </div>
                ))}
                <div style={{display:"flex",gap:10,marginTop:8}}>
                  <button onClick={closeSurvey} style={{flex:1,padding:"10px",border:"1px solid #2B2A26",borderRadius:10,background:"#FBF6EA",cursor:"pointer",fontSize:"0.82rem",color:"#8C8677"}}>{t("quick.cancel")}</button>
                  <button onClick={submitSurvey} disabled={surveyAns.some(a=>a<0)}
                    style={{flex:2,padding:"10px",border:"none",borderRadius:10,background:surveyAns.some(a=>a<0)?"#EAE3D2":"#B8862A",color:"#fff",cursor:surveyAns.some(a=>a<0)?"not-allowed":"pointer",fontSize:"0.82rem",fontWeight:700}}>
                    {surveyAns.some(a=>a<0)?t("quick.questionsLeft", { n: surveyAns.filter(a=>a<0).length }):t("quick.estimateHour")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{fontSize:"0.83rem",fontWeight:700,color:"#2B2A26",marginBottom:10}}>{t("quick.surveyResultTitle")}</div>
                {surveyResults.map((r) => (
                  <div key={r.rank} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 14px",borderRadius:10,marginBottom:7,
                    background:r.rank===1?"#FFF9DB":r.rank===2?"#F3F0FF":"#F0F8FF",
                    border:`1.5px solid ${r.rank===1?"#B8862A":r.rank===2?"#9775FA":"#74C0FC"}`}}>
                    <div style={{width:26,height:26,borderRadius:"50%",background:r.rank===1?"#B8862A":r.rank===2?"#9775FA":"#74C0FC",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.75rem",fontWeight:800,flexShrink:0}}>{r.rank}</div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:800,fontSize:"0.88rem",color:"#2b1d10"}}>{r.name} ({r.hanja}) — {r.time_range}</div>
                      <div style={{fontSize:"0.75rem",color:"#888",marginTop:1}}>{t("quick.surveyMeta", { ohaeng: r.ohaeng, animal: r.animal, confidence: r.confidence })}</div>
                    </div>
                    <button onClick={() => applySurveyResult(r.birth_hour)}
                      style={{padding:"6px 12px",border:"none",borderRadius:8,background:"#B8862A",color:"#fff",fontSize:"0.75rem",fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                      {t("quick.setThisTime")}
                    </button>
                  </div>
                ))}
                <div style={{display:"flex",gap:10,marginTop:10}}>
                  <button onClick={() => setSurveyResults([])} style={{flex:1,padding:"9px",border:"1px solid #2B2A26",borderRadius:10,background:"#FBF6EA",cursor:"pointer",fontSize:"0.8rem",color:"#8C8677"}}>{t("quick.retake")}</button>
                  <button onClick={closeSurvey} style={{flex:1,padding:"9px",border:"1px solid #2B2A26",borderRadius:10,background:"#FBF6EA",cursor:"pointer",fontSize:"0.8rem",color:"#8C8677"}}>{t("quick.close")}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <DancheongBar />
      <Wrap>
        {/* ── STEP 0 히어로: 전체 씬 이미지 ── */}
        {step === 0 && (
          <HeroSceneWrap>
            <HeroSceneImg src="/woo_06.webp" alt={t("quick.altHero")} />
            <HeroSceneOverlay />
            <HeroTitleBlock>
              <HeroTitle>{t("quick.brand")}</HeroTitle>
              <HeroSub>{t("quick.heroSub")}</HeroSub>
            </HeroTitleBlock>
          </HeroSceneWrap>
        )}

        {/* 네비게이션 */}
        <NavBar style={{ marginTop: step === 1 ? 0 : 0 }}>
          <NavBtn onClick={() => navigate("/")}>{t("quick.home")}</NavBtn>
          {step === 1 && (
            <NavBtn onClick={() => { setStep(0); setErr(""); }}>{t("quick.selectService")}</NavBtn>
          )}
          <PageTitle>{step === 0 ? t("quick.chooseFortune") : t("quick.brand")}</PageTitle>
        </NavBar>

        {/* 지난번에 결제하고 결과를 못 받은 게 있으면 입력 화면에서도 맨 위에 보인다 */}
        {paidPending && <div style={{ padding: "0 16px" }}>{paidPendingBox}</div>}

        {/* ── STEP 1 헤더: 선택한 서비스 씬 이미지 ── */}
        {step === 1 && svc && (
          <HeroSceneWrap>
            <HeroSceneImg src={svc.img || "/woo_06.webp"} alt={svc.label} />
            <HeroSceneOverlay />
            <HeroTitleBlock>
              <HeroTitle>{svcLabel(svc.key)}</HeroTitle>
              <HeroSub>{t("quick.heroDeliverySub")}</HeroSub>
            </HeroTitleBlock>
          </HeroSceneWrap>
        )}

        {/* ── STEP 0: 서비스 선택 ── */}
        {step === 0 && (
          <>
            <CardGrid>
              {SERVICES.map((s, idx) => (
                <SvcCard key={s.key} $color={s.color} $idx={idx} onClick={() => selectService(s.key)}>
                  <SvcImgBanner src={s.img} alt="" />
                  <SvcCardBody $color={s.color}>
                    <SvcHeadTitle>{svcLabel(s.key)}</SvcHeadTitle>
                    <SvcDesc $color={s.color}>{svcDesc(s.key)}</SvcDesc>
                  </SvcCardBody>
                </SvcCard>
              ))}
            </CardGrid>
            <SiteFooter />
          </>
        )}

        {/* ── STEP 1: 입력 폼 ── */}
        {step === 1 && svc && (
          <FormArea>
            {/* 선택된 서비스 배지 */}
            <ServiceBadge $color={svc.color}>
              <img loading="lazy" decoding="async" src={svc.iconImg} alt={svc.label} style={{ width: 40, height: 40, objectFit: "contain", filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.18))", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 900, color: svc.color, fontSize: 14 }}>
                  {t("quick.badgeService", { svc: svcLabel(svc.key) })}
                </div>
                {svc.ageLimit && (
                  <div style={{ fontSize: 11, color: "#e03131", fontWeight: 700, marginTop: 2 }}>
                    {t("quick.ageLimitNote")}
                  </div>
                )}
              </div>
            </ServiceBadge>

            {/* 내 정보 폼 */}
            <PersonFormBlock
              title={t("quick.myInfo")}
              data={person1}
              onChange={setPerson1}
              accentColor={svc.color}
              email={email}
              onEmailChange={setEmail}
              onSurvey={() => openSurveyFor("p1")}
            />

            {/* 상대방 정보 (연애궁합만) */}
            {svc.needPartner && (
              <>
                <div style={{ textAlign: "center", fontSize: 12, color: "#9a8aaa", margin: "4px 0 12px", fontWeight: 600 }}>
                  {t("quick.partnerPrompt")}
                </div>
                <PersonFormBlock
                  title={t("quick.partnerInfo")}
                  data={person2}
                  onChange={setPerson2}
                  accentColor={svc.color}
                  onSurvey={() => openSurveyFor("p2")}
                />
              </>
            )}

            {/* 이용약관 동의 */}
            <Card style={{ marginTop: 0 }}>
              <ConsentBox>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                />
                <span>
                  {t("quick.consentPrefix")}
                  <a href="/legal/terms" target="_blank" rel="noreferrer" style={{ color: "#8C8677" }}>{t("quick.terms")}</a>
                  {t("quick.consentMid")}
                  <a href="/legal/privacy" target="_blank" rel="noreferrer" style={{ color: "#8C8677" }}>{t("quick.privacy")}</a>
                  {t("quick.consentSuffix")}
                </span>
              </ConsentBox>
            </Card>

            {/* 리포트는 재발급이 안 되므로 입력 단계에서 미리 안내 */}
            <SaveNotice style={{ margin: "0 0 12px" }}>
              <SaveNoticeTitle>⚠️ {t("common.saveWarnTitle")}</SaveNoticeTitle>
              <SaveNoticeBody>{t("common.saveWarnBefore")}</SaveNoticeBody>
            </SaveNotice>

            {err && <ErrMsg>{err}</ErrMsg>}

            <SubmitBtn $color={svc.color} onClick={handlePreview} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <img loading="lazy" decoding="async" src="/woo_y_1.webp" alt="" style={{ width: 28, height: 28, objectFit: "contain", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }} />
              {t("quick.previewTitle", { svc: svcLabel(svc.key) })}
            </SubmitBtn>
            <div style={{ fontSize: 11, color: "#9a8aaa", textAlign: "center", marginTop: 8 }}>
              {t("quick.previewNote")}
            </div>
            <div style={{ fontSize: 11, color: "#a08060", textAlign: "center", marginTop: 6 }}>
              {t("quick.refundNotice2Before")}<a href="/legal/refund" target="_blank" rel="noreferrer" style={{ color: "#8a6a9a", textDecoration: "underline" }}>{t("quick.refundPolicy")}</a>{t("quick.refundNotice2After")}
            </div>
          </FormArea>
        )}
      </Wrap>
    </>
  );
}
